import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import { definitions, operations } from '@luxuryescapes/contract-svc-promo';

import { getPromoPlacesAtCoordinate } from '~/services/PromoService';

import CopyableField from '../Common/CopyableField';
import PageSubheader from '../Common/Elements/PageSubheader';

const PLACE_ID_CONDITION_KEY: definitions['PromoCodeConditionPresented']['condition_type'] = 'PLACE-ID-VALIDATION';

export { PLACE_ID_CONDITION_KEY };

type NormalizedPlace = { placeId: string; displayName: string };

/** Success JSON for GET /api/promo/places/at-coordinate (keep in sync with contract). */
type PlaceIdsAtCoordinateBody =
  operations['getPlaceIdsAtCoordinate']['responses']['200']['content']['application/json'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/** Parses "lat, lng" pairs (e.g. from Google Maps) pasted into the latitude field */
function parseLatLngPair(text: string): { lat: string; lng: string } | null {
  const t = text.trim();
  const m = t.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
  if (!m) {
    return null;
  }
  return { lat: m[1], lng: m[2] };
}

/**
 * Normalises {@link PlaceIdsAtCoordinateBody} for the UI: `result.place_ids` plus optional display names from
 * `result.places[].le_place_id` / `le_place_name`. We only accept this contract shape (not legacy or alternate
 * payloads); mismatches yield [] and the caller shows the raw body snippet for debugging.
 */
function extractPlacesFromResponse(body: unknown): Array<NormalizedPlace> {
  if (!isRecord(body)) {
    return [];
  }
  const result = body.result;
  if (!isRecord(result)) {
    return [];
  }

  const placeIds = result.place_ids;
  if (!Array.isArray(placeIds) || !placeIds.every((id): id is string => typeof id === 'string')) {
    return [];
  }

  const nameById = new Map<string, string>();
  const placesForLabels = result.places;
  if (Array.isArray(placesForLabels)) {
    for (const item of placesForLabels) {
      if (!isRecord(item)) {
        continue;
      }
      const id = item.le_place_id;
      const label = item.le_place_name;
      if (typeof id === 'string' && id && typeof label === 'string' && label) {
        nameById.set(id, label);
      }
    }
  }

  return placeIds.map((placeId) => ({ placeId, displayName: nameById.get(placeId) ?? placeId }));
}

type Props = {
  /** Default collapsed margin when embedded in dense layouts */
  compact?: boolean;
};

export default function PlaceIdCoordinateLookupHelper({ compact = false }: Props) {
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawBody, setRawBody] = useState<unknown>(null);
  const [longRawBody, setLongRawBody] = useState<unknown>(null);
  const [places, setPlaces] = useState<Array<NormalizedPlace>>([]);

  const canSubmit = useMemo(() => {
    const la = parseFloat(lat);
    const ln = parseFloat(lng);
    return Number.isFinite(la) && Number.isFinite(ln);
  }, [lat, lng]);

  const onLookup = useCallback(async () => {
    if (!canSubmit) {
      setError('Enter valid latitude and longitude numbers.');
      return;
    }
    setLoading(true);
    setError(null);
    setRawBody(null);
    setPlaces([]);
    try {
      const la = parseFloat(lat);
      const ln = parseFloat(lng);
      const res = await getPromoPlacesAtCoordinate(la, ln);
      setRawBody(res);
      setLongRawBody(res);
      setPlaces(extractPlacesFromResponse(res));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Request failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [canSubmit, lat, lng]);

  return (
    <Box
      sx={{
        p: compact ? 1 : 1.5,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <PageSubheader title="Place ID Lookup Helper" />
      <Typography variant="subtitle2" gutterBottom>
        Look up place IDs at coordinates. Get a lat/lng pair from google maps (right click to copy the lat/lng pair)
        into the "Latitude" field to search for placeIds.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start" useFlexGap>
        <TextField
          size="small"
          label="Latitude"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          placeholder="-33.8688 or lat, lng"
          onPaste={(e) => {
            const text = e.clipboardData.getData('text/plain');
            const pair = parseLatLngPair(text);
            if (pair) {
              e.preventDefault();
              setLat(pair.lat);
              setLng(pair.lng);
            }
          }}
        />
        <TextField
          size="small"
          label="Longitude"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          placeholder="151.2093"
        />
        <Button
          variant="outlined"
          size="small"
          onClick={onLookup}
          disabled={loading || !canSubmit}
          sx={{ mt: { sm: 0.5 } }}
        >
          {loading ? <CircularProgress size={18} /> : 'Look up'}
        </Button>
      </Stack>
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      {places.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.5 }} alignItems="flex-start">
          <Typography variant="caption" color="text.secondary">
            Place IDs (copy to paste into the condition value)
          </Typography>
          {places.map((p) => (
            <CopyableField
              key={p.placeId}
              value={p.placeId}
              label={
                p.displayName === p.placeId ? (
                  p.placeId
                ) : (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.25,
                    }}
                  >
                    <Typography component="span" variant="body2" sx={{ lineHeight: 1.3 }}>
                      {p.displayName}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {p.placeId}
                    </Typography>
                  </Box>
                )
              }
              variant="body2"
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
